import type { PodBrand } from '@/types/pod'

/**
 * The Kernow.ai pod — branding layer (browser side).
 *
 * THE OPERATOR'S OWN SITE (kernow.ai, Mick's Cornwall-facing brand, run by
 * Autaimate). RE-NICHED 15 Oct 2026 to Cornwall's construction trades:
 * electricians, plumbers and heating engineers, builders, roofers, renewables
 * installers, kitchen and bathroom fitters, and drainage and septic tank
 * installers — plus Kelvo, the staff-side product for contractors with staff. Embedded as an iframe from
 * chat.kernow.ai on kernow.ai only (EMBED_PARENT_ORIGIN = https://kernow.ai).
 * Operator-owned, like the foredesk pod: billing 'operator', no customerSince,
 * no DPA — the operator is the controller here, not a processor.
 *
 * IT IS ALSO THE LIVE DEMO. A site selling AI front desks must run one, so this
 * assistant is the product answering for itself, in Cornwall's own words.
 *
 * ASSISTANT NAME. "Kensa" — Cornish for "first": the first to greet a visitor.
 * Chosen by the operator 10 Oct 2026. Distinct from Bell (Foredesk), Piran
 * (Cornwall Play Safety) and Jago (Cornwall Steel Buildings).
 *
 * PALETTE. Lifted from kernow.ai's own tokens (src/styles/global.css, @theme)
 * so the panel and the page that frames it read as one brand: Atlantic teal
 * `sea`, its `foam` hover, the sea-mist `ink` ground, granite `paper` type,
 * `granite` muted type and the `ink-3` hairline. The accent is the granite ink
 * the St Piran cross sits beside; the site's danger red does not exist, so the
 * engine's shared one is kept.
 *
 * PRIVACY LINK. kernow.ai/privacy, not the engine's /privacy: on the operator's
 * own site the operator is the controller, not a processor (same reasoning as
 * the csb pod). That page now describes Kensa: what she keeps, Cloudflare and
 * Anthropic as processors, and that she never texts the visitor.
 *
 * CONTACT. The site's own, from kernow-ai src/consts.ts (SITE), phone
 * confirmed by the operator 10 Oct 2026: 07501 439406, hello@kernow.ai.
 *
 * TAGLINE and WELCOME. Trades-first, matching the site's titles since the
 * re-niche ("AI & Automation for Cornwall Trades"). Kelvo is named so a
 * contractor with staff knows to ask; it carries no price anywhere.
 * The SMS notify target (the operator's other mobile) belongs in
 * workers/pod.ts, never here.
 *
 * POSITIONING (same rule as the foredesk pod): never the word the sites ban;
 * the conversation is how it works, not what it is.
 */
export const kernowPod: PodBrand = {
  id: 'kernow',
  brandName: 'Kernow.ai',
  productName: 'Kernow.ai',
  assistantName: 'Kensa',
  tagline: 'AI & automation for Cornwall’s trades',
  roleLabel: 'front desk',
  welcomeIntro:
    "I can tell you what an AI front desk does for a Cornwall trade business, what it costs and how setup works, and about Kelvo, which answers your own staff's questions if you run a firm with a team. If you'd like either, I'll take your details and Mick will call you back. What would you like to know?",
  starters: [
    'What does the AI front desk do?',
    'How much does it cost?',
    'What is Kelvo?',
    'Can I see one working?',
  ],
  inputPrompt: 'Ask Kensa about Kernow.ai',
  footerNote:
    "Kensa answers only from Kernow.ai's own published information. Anything outside it goes straight to Mick.",
  theme: {
    // kernow.ai src/styles/global.css @theme tokens.
    '--color-brand-primary': '15 118 110', // sea #0F766E
    '--color-brand-primary-hover': '11 95 88', // foam #0B5F58
    '--color-brand-accent': '28 37 40', // paper (granite ink) #1C2528
    '--color-surface-base': '245 247 245', // ink (sea-mist ground) #F5F7F5
    '--color-surface-raised': '255 255 255', // ink-2 #FFFFFF
    '--color-surface-subtle': '245 247 245', // ink #F5F7F5
    '--color-text-primary': '28 37 40', // paper #1C2528
    '--color-text-muted': '85 97 93', // granite #55615D
    '--color-text-danger': '178 47 47',
    '--color-border-subtle': '217 224 219', // ink-3 #D9E0DB
  },
  // The site's own notice covers this chat once updated. See PodBrand.privacyUrl.
  privacyUrl: 'https://kernow.ai/privacy',
  // The operator's own product on the operator's own site — the credit is the
  // point here, not an advert on someone else's page.
  builtByForedesk: true,
  contact: {
    phone: '07501 439406',
    email: 'hello@kernow.ai',
  },
}
